import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IntegrationSlug, SiteIntegration, SiteIntegrationEntryProps } from '@ixicare/resources';
import { t } from '~i18n';
import { useStores } from '~store/utils';

const queryKey = (siteUuid?: string) => ['site-integrations', siteUuid];

export const useSiteIntegrations = (siteUuid: string | undefined) =>
  useQuery<SiteIntegrationEntryProps[]>({
    queryKey: queryKey(siteUuid),
    queryFn: () => SiteIntegration.getSiteIntegrations(siteUuid!),
    enabled: !!siteUuid,
  });

/**
 * The integrations of one site. The backend only returns integrations that were ever set up, so
 * a missing entry means disabled.
 */
export const useSiteIntegration = (siteUuid: string | undefined, integrationSlug: IntegrationSlug) => {
  const queryClient = useQueryClient();
  const { notifications } = useStores();

  const query = useSiteIntegrations(siteUuid);

  const mutation = useMutation({
    mutationFn: (props: { isEnabled: boolean; metaInfo?: Record<string, unknown> }) =>
      SiteIntegration.putSiteIntegration(siteUuid!, integrationSlug, props),
    onSuccess: (data) => {
      queryClient.setQueryData<SiteIntegrationEntryProps[]>(queryKey(siteUuid), (old) => [
        ...(old ?? []).filter((integration) => integration.integrationSlug !== data.integrationSlug),
        data,
      ]);
    },
    onError: () =>
      notifications.addUserActionNotification({
        notificationType: 'error',
        message: t('site.developer.websocket.updateError'),
      }),
  });

  return {
    integration: query.data?.find((integration) => integration.integrationSlug === integrationSlug) ?? null,
    isLoading: query.isLoading,
    update: mutation,
  };
};
